import {
  DATA_SESSION_UPLOAD_URL,
  DATA_SESSION_UPDATE_URL,
  UPFLOWY_ANONYMOUS_USER,
  MAX_COOKIE_EXPIRING_DATE,
} from '../settings';
import { ContactDetails, Persistence } from '../types';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import UuidGenerator from './UuidGenerator';

export type SessionPayload = {
  id: string;
  contacts: ContactDetails[];
  metadata: Record<string, unknown>;
};

type SessionHandlerConfig = {
  persistence: Persistence;
  workspaceId: string;
};

class AnonymousUser {
  private config: SessionHandlerConfig;

  private uploadedSessionIds: Set<string>;

  private storageKey = UPFLOWY_ANONYMOUS_USER;

  private anonymousUserId: string;

  private updateSessionQueue: { payload: SessionPayload; resolve: () => void }[] = [];

  private isProcessingQueue: boolean = false;

  constructor(config: SessionHandlerConfig) {
    this.config = config;
    const anonymousUserStorageKey = `${config.workspaceId}${this.storageKey}`;
    const stored: string = config.persistence.get(anonymousUserStorageKey) ?? '';
    if (stored) {
      const [anonymousUserId, sessionIds] = stored.split(this.config.persistence.separator);
      this.anonymousUserId = anonymousUserId;
      this.uploadedSessionIds = sessionIds ? new Set(sessionIds.split(',')) : new Set();
    } else {
      this.anonymousUserId = UuidGenerator.generate();
      this.uploadedSessionIds = new Set();
      this.persistAnonymousUser();
    }
  }

  getAnonymousUserId() {
    return this.anonymousUserId;
  }

  persistAnonymousUser() {
    const ids = Array.from(this.uploadedSessionIds).join(',');
    const anonymousUserStorageKey = `${this.config.workspaceId}${this.storageKey}`;
    this.config.persistence.set(
      anonymousUserStorageKey,
      `${this.anonymousUserId}${this.config.persistence.separator}${ids}`,
      MAX_COOKIE_EXPIRING_DATE,
    );
  }

  async syncSession(payload: SessionPayload) {
    if (this.uploadedSessionIds.has(payload.id)) {
      await this.addToUpdateSessionQueue(payload);
    } else {
      this.uploadedSessionIds.add(payload.id);
      this.persistAnonymousUser();
      await this.uploadSession(payload).catch(() => {
        this.uploadedSessionIds.delete(payload.id);
        this.persistAnonymousUser();
      });
    }
  }

  private async addToUpdateSessionQueue(payload: SessionPayload) {
    const wasQueueEmpty = this.updateSessionQueue.length === 0;

    return new Promise<void>((resolve) => {
      this.updateSessionQueue.push({ payload, resolve });

      // Trigger processing if the queue was previously empty and not already processing
      if (wasQueueEmpty && !this.isProcessingQueue) {
        this.processUpdateSessionQueue();
      }
    });
  }

  private async processUpdateSessionQueue() {
    this.isProcessingQueue = true;

    if (this.updateSessionQueue.length > 0) {
      const queueItem = this.updateSessionQueue.shift();
      if (!queueItem) {
        return;
      }
      const { payload, resolve } = queueItem;

      await this.updateSession(payload);
      resolve();

      // Continue processing the queue recursively
      this.processUpdateSessionQueue();
    } else {
      // If the queue is empty, set the processing flag to false
      this.isProcessingQueue = false;
    }
  }

  async uploadSession(payload: SessionPayload) {
    return fetchWithTimeout(DATA_SESSION_UPLOAD_URL, {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        anonymousUserId: this.anonymousUserId,
        workspaceId: this.config.workspaceId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  async updateSession(payload: SessionPayload) {
    return fetchWithTimeout(DATA_SESSION_UPDATE_URL, {
      method: 'PUT',
      body: JSON.stringify({
        ...payload,
        anonymousUserId: this.anonymousUserId,
        workspaceId: this.config.workspaceId,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60 * 1000,
    });
  }
}

export default AnonymousUser;
